import { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Spin, Table, Tag } from "antd";
import { SafetyCertificateOutlined, SaveOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import apiClient from "../../actions/api";
import { loadPagePermissions } from "../../actions/permissions";
import { PAGE_LABELS } from "../../config/pagePermissions";
import openNotification from "../../components/Notifications/openNotification";
import "../../style/user/permissions.scss";

const Permissions = ({ title }) => {
  document.title = title;
  const dispatch = useDispatch();
  const permissionState = useSelector((state) => state.permissions);
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRoles(
      permissionState.roles.map((role) => ({
        ...role,
        permissions: [...role.permissions],
      })),
    );
  }, [permissionState.roles]);

  const togglePermission = (roleNumber, pageKey, checked) => {
    setRoles((current) =>
      current.map((role) => {
        if (Number(role.role) !== Number(roleNumber)) return role;
        const permissions = checked
          ? [...new Set([...role.permissions, pageKey])]
          : role.permissions.filter((permission) => permission !== pageKey);
        return { ...role, permissions };
      }),
    );
  };

  const columns = useMemo(
    () => [
      {
        title: "Page",
        dataIndex: "label",
        key: "label",
        fixed: "left",
        width: 230,
        render: (label, record) => (
          <div className="permission-page-name">
            <strong>{label}</strong>
            <span>{record.key}</span>
          </div>
        ),
      },
      ...roles.map((role) => ({
        title: (
          <Tag color={Number(role.role) === 1 ? "red" : "blue"}>
            {role.name}
          </Tag>
        ),
        key: `role-${role.role}`,
        width: 125,
        align: "center",
        render: (_, record) => (
          <Checkbox
            aria-label={`Allow ${role.name} to access ${record.label}`}
            checked={role.permissions.includes(record.key)}
            onChange={(event) =>
              togglePermission(role.role, record.key, event.target.checked)
            }
          />
        ),
      })),
    ],
    [roles],
  );

  const rows = permissionState.pageKeys.map((key) => ({
    key,
    label: PAGE_LABELS[key] || key,
  }));

  const savePermissions = async () => {
    setSaving(true);
    try {
      await apiClient.put("/permissions", {
        roles: roles.map(({ role, permissions }) => ({ role, permissions })),
      });
      await dispatch(loadPagePermissions());
      openNotification(
        "success",
        "Permissions updated",
        "Page access rules are active now.",
      );
    } catch (error) {
      const message =
        error?.response?.data?.errors?.[0]?.msg ||
        "Unable to update permissions.";
      openNotification("error", "Update failed", message);
    } finally {
      setSaving(false);
    }
  };

  if (permissionState.loading) {
    return (
      <div className="permissions-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="permissions-section">
      <div className="permissions-heading">
        <div>
          <h2>
            <SafetyCertificateOutlined /> Page Permissions
          </h2>
          <p>
            Choose which application roles can open each page. Changes take
            effect immediately.
          </p>
        </div>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={savePermissions}
        >
          Save Permissions
        </Button>
      </div>
      <Card variant="borderless" className="permissions-card">
        <Table
          rowKey="key"
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 730 }}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default Permissions;
